import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { FileStack, Plus, ArrowRight, Trash2, Calendar, Building, Copy, Search, ChevronDown } from "lucide-react";

const today = () => {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

export const STATUSES = [
  { value: "Draft", cls: "bg-slate-100 text-slate-600 hover:bg-slate-100" },
  { value: "Submitted", cls: "bg-blue-50 text-blue-700 hover:bg-blue-50" },
  { value: "Won", cls: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50" },
];
export const statusCls = (s) => (STATUSES.find((x) => x.value === s) || STATUSES[0]).cls;

export default function Bids() {
  const [bids, setBids] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ bid_number: "", bid_date: today(), buyer_name: "", buyer_block: "", product_name: "", quantity: "" });
  const navigate = useNavigate();

  const load = () => api.get("/bids").then((r) => setBids(r.data));
  useEffect(() => {
    load();
    api.get("/buyers").then((r) => setBuyers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const submit = async () => {
    if (!form.bid_number.trim()) return toast.error("Bid number is required");
    if (!form.buyer_name.trim()) return toast.error("Buyer / Department is required");
    try {
      const r = await api.post("/bids", {
        bid_number: form.bid_number,
        bid_date: form.bid_date,
        buyer_name: form.buyer_name.trim(),
        buyer_block: (form.buyer_block.trim() || form.buyer_name.trim()),
        product_name: form.product_name.trim(),
        quantity: form.quantity,
      });
      toast.success("Bid created");
      setOpen(false);
      navigate(`/bids/${r.data.id}`);
    } catch { toast.error("Could not create bid"); }
  };

  const pickBuyer = (id) => {
    const b = buyers.find((x) => x.id === id);
    if (b) setForm((f) => ({ ...f, buyer_name: b.name, buyer_block: b.address_block || b.name }));
  };
  const pickProduct = (id) => {
    const p = products.find((x) => x.id === id);
    if (p) setForm((f) => ({ ...f, product_name: p.name }));
  };

  const remove = async (e, id) => {
    e.stopPropagation();
    await api.delete(`/bids/${id}`);
    toast.success("Bid deleted");
    load();
  };

  const duplicate = async (e, id) => {
    e.stopPropagation();
    try {
      const r = await api.post(`/bids/${id}/duplicate`);
      toast.success("Bid duplicated — edit the details for your new tender");
      navigate(`/bids/${r.data.id}`);
    } catch { toast.error("Could not duplicate bid"); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/bids/${id}/status`, { status });
      setBids((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success(`Marked as ${status}`);
    } catch { toast.error("Could not update status"); }
  };

  const filtered = bids.filter((b) => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return [b.bid_number, b.buyer_name, b.product_name].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="px-6 lg:px-12 py-8 lg:py-12 max-w-6xl">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
            <FileStack className="h-5 w-5 text-slate-900" />
          </div>
          <div>
            <h1 className="font-head text-2xl sm:text-3xl font-extrabold tracking-tight">Bids & Documents</h1>
            <p className="text-sm text-slate-500">Create a bid, then generate all required documents inside it.</p>
          </div>
        </div>
        <Button onClick={() => setOpen(true)} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="create-bid-open-btn">
          <Plus className="h-4 w-4 mr-2" /> New Bid
        </Button>
      </div>

      {bids.length > 0 && (
        <div className="relative mt-6 max-w-md">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="bid-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by bid number, buyer or product…"
            className="pl-9 rounded-full"
          />
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mt-6">
        {filtered.map((b) => (
          <Card
            key={b.id}
            data-testid={`bid-card-${b.id}`}
            onClick={() => navigate(`/bids/${b.id}`)}
            className="p-5 rounded-xl border-border cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all duration-150 group"
          >
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="font-head font-bold truncate">{b.product_name || "Untitled item"}</div>
                <div className="font-mono text-xs text-slate-500 mt-1">{b.bid_number}</div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-500 hover:text-emerald-700" onClick={(e) => duplicate(e, b.id)} data-testid={`duplicate-bid-${b.id}`}>
                  <Copy className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={(e) => remove(e, b.id)} data-testid={`delete-bid-${b.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1"><Building className="h-3.5 w-3.5" /> {b.buyer_name}</span>
              <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {b.bid_date}</span>
            </div>
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                <span className="text-xs rounded-full bg-emerald-50 text-emerald-700 px-2.5 py-1 font-medium">{b.doc_count} documents</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid={`bid-status-trigger-${b.id}`}
                      className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 font-medium ${statusCls(b.status || "Draft")}`}
                    >
                      {b.status || "Draft"} <ChevronDown className="h-3 w-3" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    {STATUSES.map((s) => (
                      <DropdownMenuItem key={s.value} onClick={() => setStatus(b.id, s.value)} data-testid={`bid-status-${b.id}-${s.value}`}>
                        {s.value}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <span className="inline-flex items-center text-sm font-medium text-slate-900">Open <ArrowRight className="h-4 w-4 ml-1" /></span>
            </div>
          </Card>
        ))}
        {bids.length === 0 && <div className="text-sm text-slate-500 col-span-full py-14 text-center">No bids yet. Click "New Bid" to begin.</div>}
        {bids.length > 0 && filtered.length === 0 && <div className="text-sm text-slate-500 col-span-full py-14 text-center">No bids match "{q}".</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Create New Bid</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-600">GeM Bid Number</Label>
              <Input data-testid="bid-number-input" placeholder="GEM/2026/B/7903735" value={form.bid_number} onChange={(e) => setForm({ ...form, bid_number: e.target.value })} className="mt-1.5 font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-slate-600">Bid Date</Label>
                <Input data-testid="bid-date-input" value={form.bid_date} onChange={(e) => setForm({ ...form, bid_date: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-slate-600">Quantity</Label>
                <Input data-testid="bid-quantity-input" placeholder="02" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1.5" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-600">Buyer / Department</Label>
                {buyers.length > 0 && (
                  <Select value="" onValueChange={pickBuyer}>
                    <SelectTrigger className="h-7 w-[150px] text-xs rounded-lg" data-testid="bid-buyer-select"><SelectValue placeholder="Pick from saved" /></SelectTrigger>
                    <SelectContent>
                      {buyers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Input data-testid="bid-buyer-name-input" placeholder="Type buyer / department name" value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="mt-1.5" />
              <Textarea data-testid="bid-buyer-block-input" rows={3} placeholder="Full address block (each line as it should appear under 'To,') — optional" value={form.buyer_block} onChange={(e) => setForm({ ...form, buyer_block: e.target.value })} className="mt-2 resize-none text-sm" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold text-slate-600">Product / Item</Label>
                {products.length > 0 && (
                  <Select value="" onValueChange={pickProduct}>
                    <SelectTrigger className="h-7 w-[150px] text-xs rounded-lg" data-testid="bid-product-select"><SelectValue placeholder="Pick from saved" /></SelectTrigger>
                    <SelectContent>
                      {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Input data-testid="bid-product-name-input" placeholder="Type product / item name" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Cancel</Button>
            <Button onClick={submit} className="rounded-lg bg-slate-900 hover:bg-slate-800" data-testid="create-bid-submit">Create & Open</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
